import React, { useMemo, useState } from 'react'
import { Button, Stack } from '@mantine/core'
import moment from 'moment'
import { OverviewTradeBox } from './trade-overview-box'
import SectionTitle from 'app/views/components/Headers&Text/SectionTitle'
import MainTable from 'app/views/components/Table/MainTable'
import { columnDefs } from './overview-trade-column-defs'
import { useDispatch, useSelector } from 'react-redux'
import { getMasterTrades } from 'app/state/ducks/master-trades/selectors'
import { useParams } from 'react-router-dom'
import { getFacilities, getLockedFacilities } from 'app/state/ducks/facilities/selectors'
import { tradeTypeOptions, tradeAssociationOptions, tradeDocsTypeOptions, transferTypeOptions, interestTreatmentOptions, assignmentFeePayTypeOptions, tradePaymentTypeOptions, booleanOptions } from 'app/models/dropdown-options';
import { businessDaysDiffWithHolidays, formatDateToShow, stringToDate } from 'app/utils/util-functions'
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors'
import { TradedFacilityParams } from 'app/models/traded-facilities-params'
import { showTradeId, resolveTradeHolidayDates } from '../../utils/master-trade-util'
import CancelOpenTradeModal from './cancel-open-trade-modal'
import EditTradeDetailModal from '../../edit-trade/edit-trade-detail-modal'
import EditTradeAssetsModal from '../../edit-trade/edit-trade-assets-modal'
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors'
import { masterTradeStatusLabel } from '../../utils/master-trade-status'
import { getTradeOverview } from 'app/state/ducks/trade-detail/selectors'
import { TradeOverviewFacility } from 'app/state/ducks/trade-detail/reducer'
import { useTradeRefresh } from '../use-trade-refresh'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { loadAllSubTrades } from 'app/state/ducks/sub-trades/thunks'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { loadAllFacilities, loadLockedFacilities } from 'app/state/ducks/facilities/thunks'
import { loadTradedFacilities } from 'app/state/ducks/traded-facilities/thunks'

/** A view row in the shape the Traded Assets columns read. */
const toTableRow = (f: TradeOverviewFacility) => ({
  id: f.facilityId,
  tradeAmount: f.tradeAmount,
  price: f.price,
  // the Current Amount column shows the last entry's amount
  amounts: f.currentAmount === null ? [] : [{ amount: f.currentAmount }],
  facilityDetails: {
    name: f.name,
    status: f.status,
    facilityType: f.facilityType,
    baseCurrency: f.baseCurrency,
    cusip: f.cusip,
    lxId: f.lxId,
    systemId: f.systemId,
    clientId: f.clientId,
  },
})

// What each editor reads from the stores the tab no longer loads up front
// (ADR_LOS-030, rule 5: editors fetch what they edit, on open).
const EDITOR_LOADS = {
  details: [loadEntities, loadAllEntityTypes, loadAllEntityProfiles, loadAllSubTrades],
  assets: [
    loadAllDeals,
    loadAllFacilities,
    loadLockedFacilities,
    loadTradedFacilities,
    loadAllSubTrades,
    loadEntities,
    loadAllEntityProfiles,
  ],
} as const

/**
 * The Overview tab (ADR_LOS-030): renders from its one read — the master
 * trade from the master-trade store (edits update it there in place), the
 * rest from the Overview view (`GET /api/trade-detail/:id/overview`), which
 * the page shell loads on this route.
 *
 * The editors read whole lists the view does not carry, so each Edit button
 * loads those lists first and opens when they land; closing any editor, or
 * the cancel modal, refetches the view.
 */
export function OverviewTradeEntry() {

  const { tradeId } = useParams()
  const dispatch = useDispatch()
  const { refreshOverview } = useTradeRefresh()

  const allMasterTrades = useSelector(getMasterTrades)
  const overview = useSelector(getTradeOverview)
  const holidayCalendars = useSelector(getCalendars)
  // what the edit-assets editor is handed, once its lists have loaded
  const allTradedFacilities = useSelector(getTradedFacilities)
  const facilities = useSelector(getFacilities)
  const lockedFacilities = useSelector(getLockedFacilities)

  // The view's read put this trade in the store, in whichever state it is.
  const masterTrade = useMemo(
    () => allMasterTrades.find(trade => trade.id === tradeId),
    [allMasterTrades, tradeId],
  )
  const view = overview?.masterTradeId === tradeId ? overview : null
  const myEntity = view?.myEntity
  const counterParty = view?.counterparty
  const agent = view?.agent
  const deal = view?.deal
  const tableRows = useMemo(() => (view?.facilities ?? []).map(toTableRow), [view])

  const [cancelTradeModalOpened, setCancelTradeModalOpened] = useState(false);
  const [openEditDetails, setOpenEditDetails] = useState(false)
  const [openEditAssets, setOpenEditAssets] = useState(false)
  const [preparingEditor, setPreparingEditor] = useState<'details' | 'assets' | null>(null)

  // Any settled sub trade disables Cancel for the master trade.
  const isCancelDisabled = useMemo(() =>
    !!view?.hasSettledSubTrades || masterTrade?.status !== 'Open',
    [view?.hasSettledSubTrades, masterTrade]
  )

  const holidayDates = useMemo(
    () => resolveTradeHolidayDates(masterTrade?.tradeAssociation, holidayCalendars),
    [masterTrade?.tradeAssociation, holidayCalendars],
  )
  const { closeDate, tPlus, age } = useMemo(() => {
    if (!masterTrade?.tradeDate) return { closeDate: null, tPlus: null, age: null }
    const isSettled = masterTrade.status === 'Settled'
    let closeDate: string | null = null
    let tPlus: number | null = null
    if (isSettled) {
      // the latest locked date of a settled sub trade, from the view
      closeDate = view?.closeDate ?? null
      if (closeDate) tPlus = businessDaysDiffWithHolidays(moment(masterTrade.tradeDate), moment(closeDate), holidayDates)
    }
    return {
      closeDate,
      tPlus,
      age: isSettled ? tPlus : businessDaysDiffWithHolidays(moment(masterTrade.tradeDate), moment(), holidayDates)
    }
  }, [masterTrade?.tradeDate, masterTrade?.status, view?.closeDate, holidayDates])

  // The edit-assets editor's traded facilities, as the tab used to build them
  // from the stores (sorted by facility name).
  const editorTradedFacilities = useMemo(() => {
    const allFacilities = [...facilities, ...lockedFacilities]
    return allTradedFacilities
      .filter(facility => facility.contractId?.id === tradeId)
      .map(facility => ({
        ...facility,
        id: facility.facilityId.id,
        facilityDetails: allFacilities.find(f => f.id === facility.facilityId.id) ?? null,
      }))
      .sort((a, b) => {
        const nameA = a.facilityDetails?.name?.toLowerCase() || ''
        const nameB = b.facilityDetails?.name?.toLowerCase() || ''
        return nameA.localeCompare(nameB)
      }) as TradedFacilityParams[]
  }, [tradeId, allTradedFacilities, facilities, lockedFacilities])

  // Load what the editor edits, then open it.
  const openEditor = async (editor: 'details' | 'assets') => {
    if (!tradeId || preparingEditor) return
    setPreparingEditor(editor)
    try {
      await Promise.all(EDITOR_LOADS[editor].map(load => Promise.resolve(dispatch(load() as any))))
    } finally {
      setPreparingEditor(null)
    }
    if (editor === 'details') setOpenEditDetails(true)
    else setOpenEditAssets(true)
  }

  // An edit changes what the view joins and computes (counterparty, trade
  // date, traded facilities, status), so closing an editor refetches it.
  const closesWithRefresh = (
    isOpen: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  ): React.Dispatch<React.SetStateAction<boolean>> => (value) => {
    const open = typeof value === 'function' ? value(isOpen) : value
    setOpen(open)
    if (!open) refreshOverview()
  }
  const setEditDetailsOpen = closesWithRefresh(openEditDetails, setOpenEditDetails)
  const setEditAssetsOpen = closesWithRefresh(openEditAssets, setOpenEditAssets)

  const buildSettlementMethodString = () => {
    let masterTradeString = ''
    if (masterTrade?.settlementMethod) {
      masterTradeString += masterTrade?.settlementMethod
      if (showTradeId(masterTrade?.settlementMethod) && masterTrade?.outsideTradeId) {
        masterTradeString += " (" + masterTrade?.outsideTradeId + ")"
      }
    }
    return masterTradeString
  }

  return (
    <>
      <Stack style={{ gap: '30px' }}>
        <div
          className='trade-flex-traded-assets-container'
        >
          <SectionTitle>Traded Details</SectionTitle>
          <div
            className="trade-flex-edit-button-block"
            style={{ gap: 12, alignItems: 'center' }}
          >
            <Button
              disabled={masterTrade?.status !== 'Open'}
              loading={preparingEditor === 'details'}
              onClick={() => openEditor('details')}
              className="edit-trade-details-button"
            >
              Edit
            </Button>
          </div>
          <div className='trade-flex-container-table'>
            <div className='trade-flex-container-row'>
              <OverviewTradeBox
                name="Booking"
                column={2}
                data={[
                  { key: "Trade ID:", value: masterTrade?.systemId ?? '' },
                  { key: "Trade Status:", value: masterTradeStatusLabel(masterTrade?.status) },
                  { key: "Internal Trade ID:", value: masterTrade?.clientId ?? '' },
                  { key: "Trade Date:", value: masterTrade?.tradeDate ? formatDateToShow(stringToDate(masterTrade.tradeDate)) : '' },
                  { key: "Trade Group ID:", value: masterTrade?.tradeGroupId ?? '' },
                  { key: "Trade Action:", value: masterTrade?.myEntity ?? '' },
                  { key: "Settlement Method:", value: buildSettlementMethodString() },
                  { key: "Close Date:", value: closeDate ? formatDateToShow(stringToDate(closeDate)) : '' },
                  { key: "", value: "" }, // Filler to bump T+ to right column
                  { key: "T+:", value: tPlus != null ? tPlus.toString() : '' },
                  { key: "", value: "" }, // Filler to bump Age to right column
                  { key: "Age:", value: age != null ? age.toString() : '' },
                ]}
                onCancelTradeClick={() => setCancelTradeModalOpened(true)}
                isCancelDisabled={isCancelDisabled}
              />
              <OverviewTradeBox
                name="References"
                column={2}
                data={[
                  { key: "Trader:", value: masterTrade?.trader ?? '' },
                  { key: "Closer:", value: masterTrade?.closer ?? '' },
                  { key: "PM:", value: masterTrade?.pm ?? '' },
                  { key: "Counterparty Closer:", value: masterTrade?.counterpartyCloser ?? '' },
                  { key: "Desk:", value: masterTrade?.desk ?? '' }
                ]}
              />
            </div>

            <div className='trade-flex-container-row'>
              <OverviewTradeBox
                name={`My Entity (${masterTrade?.myEntity === 'Buy' ? 'Buyer' : 'Seller'})`}
                column={1}
                data={[
                  { key: "Name:", value: myEntity?.name ?? '' },
                  { key: "MEI:", value: myEntity?.mei ?? '' },
                  { key: "Entity Type:", value: myEntity?.entityType ?? '' },
                  { key: "LendOS ID:", value: myEntity?.systemId ?? '' },
                  { key: "Internal ID:", value: myEntity?.internalId ?? '' }
                ]}
              />
              <OverviewTradeBox
                name={`Counterparty (${masterTrade?.myEntity === 'Buy' ? 'Seller' : 'Buyer'})`}
                column={1}
                data={[
                  { key: "Name:", value: counterParty?.name ?? '' },
                  { key: "MEI:", value: counterParty?.mei ?? '' },
                  { key: "Entity Type:", value: counterParty?.entityType ?? '' },
                  { key: "LendOS ID:", value: counterParty?.systemId ?? '' },
                  { key: "Internal ID:", value: counterParty?.internalId ?? '' }
                ]}
              />
              <OverviewTradeBox
                name="Agent"
                column={1}
                data={[
                  { key: "Name:", value: agent?.name ?? '' },
                  { key: "MEI:", value: agent?.mei ?? '' },
                  { key: "Entity Type:", value: agent?.entityType ?? '' },
                  { key: "LendOS ID:", value: agent?.systemId ?? '' },
                  { key: "Internal ID:", value: agent?.internalId ?? '' }
                ]}
              />
            </div>
            {/* <div className='trade-flex-terms-of-trade-container'> */}
            <div className='trade-flex-container-row'>
              <OverviewTradeBox
                name="Terms of trade"
                column={3}
                data={[
                  { key: "Trade Type:", value: tradeTypeOptions.find(({ value }) => masterTrade?.tradeType === value)?.label ?? '' },
                  { key: "Trade Docs:", value: tradeDocsTypeOptions.find(({ value }) => masterTrade?.tradeDocsType === value)?.label ?? '' },
                  { key: "Interest Treatment:", value: interestTreatmentOptions.find(({ value }) => masterTrade?.interestTreatment === value)?.label ?? '' },
                  { key: "Trade Association:", value: tradeAssociationOptions.find(({ value }) => masterTrade?.tradeAssociation === value)?.label ?? '' },
                  { key: "Transfer Type:", value: transferTypeOptions.find(({ value }) => masterTrade?.transferType === value)?.label ?? '' },
                  { key: "Assignment Fee Split:", value: assignmentFeePayTypeOptions.find(({ value }) => masterTrade?.assignmentFeePayType === value)?.label ?? '' },
                  { key: "Payment:", value: tradePaymentTypeOptions.find(({ value }) => masterTrade?.paymentType === value)?.label ?? '' },
                  { key: "Disable DC/CoC:", value: booleanOptions.find(({ value }) => masterTrade?.disableDCCoC === value)?.label ?? '' },
                ]}
              />
            </div>
          </div>
        </div>
        <div
          className='trade-flex-traded-assets-container'
        >
          <SectionTitle>Traded Assets</SectionTitle>
          <div className="trade-flex-edit-button-block">
            <Button
              disabled={masterTrade?.status !== 'Open'}
              loading={preparingEditor === 'assets'}
              onClick={() => openEditor('assets')}
              className="edit-trade-details-button"
            >
              Edit
            </Button>
          </div>
          <div className="trade-flex-deal-container" >
            <div className='trade-flex-container-row'>
              <OverviewTradeBox
                name="Traded Deal"
                column={2}
                data={[
                  { key: "Deal Name:", value: deal?.name ?? '', span: true },
                  { key: "LendOS ID:", value: deal?.systemId ?? '' },
                  { key: "CUSIP:", value: deal?.cusip ?? '' },
                  { key: "Internal ID:", value: '' },
                  { key: "LX ID:", value: deal?.lxId ?? '' },
                ]}
              />
            </div>
            <div
              className='trade-flex-facility-table'
            >
              <MainTable
                stretch={true}
                tableName={"Facility Traded Assets"}
                columnDefs={columnDefs}
                data={tableRows as any}
                enableTopToolbar={false}
                headerBackgroundColor='#F0EEEE'
                withPagination={false}
                enableColumnFilters={true}
                showBottomBoolean={false}
                border={true}
                enableColumnOrdering={false}
                enableColumnResizing={false}
                enableGrouping={false}
                enableColumnActions={true}
                enableSorting={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                minHeight='fit-content'
                setRow={() => { }}
              />
            </div>
          </div>
        </div>
      </Stack >
      {openEditAssets &&
        <EditTradeAssetsModal
          open={openEditAssets}
          tradedFacilities={editorTradedFacilities}
          setOpen={setEditAssetsOpen}
          trade={masterTrade}
        />
      }
      {openEditDetails &&
        <EditTradeDetailModal
          open={openEditDetails}
          setOpen={setEditDetailsOpen}
          tradeToEdit={masterTrade}
        />
      }
      {cancelTradeModalOpened &&
        <CancelOpenTradeModal
          opened={cancelTradeModalOpened}
          close={() => {
            setCancelTradeModalOpened(false)
            refreshOverview()
          }}
          openTradeId={{ id: masterTrade?.id ?? '', admin: masterTrade?.agencyAdmin ?? '' }}
          openTrade={masterTrade ?? ''}
        />
      }
    </>
  )
}
